/**
 * The one download runner for the Study Documents page (Jim's plan,
 * 2026-10-25). Every download button on a card, on its face and in its View
 * panel, shares one busy key: the project AND the card. While one runs the
 * others on that card are disabled and a click is answered with a "being
 * prepared" note; another study's card is unaffected.
 *
 * All the logic is in downloadRunner.ts and downloadReply.ts (pure, tested);
 * this hook only supplies the real calls, fetch, save and toasts.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { httpsCallable } from '../../lib/callable';
import { saveAs } from 'file-saver';
import { functions } from '../../firebase';
import { useToast } from '../../contexts/ToastContext';
import { busyKey, downloadBusyStore, fetchWithTimeout, runDocumentDownload, type RunnerDeps } from './downloadRunner';
import type { CallableSpec, DownloadRequest } from './studyDocumentDownloads';

async function call(spec: CallableSpec): Promise<unknown> {
  const fn = httpsCallable(functions, spec.name, spec.timeout ? { timeout: spec.timeout } : undefined);
  const result = await fn(spec.args);
  return result.data;
}

export interface DocumentDownload {
  /** Starts a download; resolves when it has finished (or was refused). */
  run: (request: DownloadRequest) => Promise<void>;
  /** The id of the request running on this card, or null. */
  runningId: string | null;
  runningSlow: boolean;
  /** Date.now() when the running download started; 0 when idle. */
  startedAt: number;
}

export function useDocumentDownload(projectId: string | undefined, card: string): DocumentDownload {
  const { showToast } = useToast();
  const key = busyKey(projectId, card);
  const current = useSyncExternalStore(downloadBusyStore.subscribe, () => downloadBusyStore.get(key));

  const run = useCallback(
    async (req: DownloadRequest) => {
      const deps: RunnerDeps = {
        store: downloadBusyStore,
        now: () => Date.now(),
        toast: (message, type) => showToast(message, type),
        call,
        // Wrapped so fetch keeps its window binding.
        fetchFile: (url, timeoutMs) => fetchWithTimeout((u, init) => fetch(u, init), url, timeoutMs),
        save: (blob, filename) => saveAs(blob, filename),
      };
      await runDocumentDownload(key, req, deps);
    },
    [key, showToast],
  );

  return {
    run,
    runningId: current?.id ?? null,
    runningSlow: current?.slow ?? false,
    startedAt: current?.startedAt ?? 0,
  };
}
